import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError, errorText } from '../api/client'
import type { ConsentBasis, NewContact, ProfilePatch } from '../api/types'
import { CONSENT_BASES, LEAD_STATUSES } from '../lib/constants'
import { IntakeFields, useVocabulary } from './IntakeFields'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { useToast } from './Toast'

const EMPTY: NewContact = { first_name: '', last_name: '', email: '', phone: '', phone_kind: 'mobile', status: 'new' }

/** Create a lead-book contact. A duplicate (same email or phone) is not an error to
 *  the officer — it's "they're already here", with a button to open them. */
export function AddContactModal({ open, onClose, initialStatus = 'new' }: {
  open: boolean
  onClose: () => void
  initialStatus?: string
}) {
  const [form, setForm] = useState<NewContact>({ ...EMPTY, status: initialStatus })
  const [profile, setProfile] = useState<ProfilePatch>({})
  const [consent, setConsent] = useState(false)
  const [basis, setBasis] = useState<ConsentBasis>('express_written')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [existing, setExisting] = useState<number | null>(null)
  // Photograph a card and the form fills itself in; the officer still checks it.
  const [scanning, setScanning] = useState(false)
  const [scanned, setScanned] = useState('')
  // Kept so the photo itself lands on the contact the scan creates.
  const [cardImage, setCardImage] = useState<{ data_base64: string; content_type: string; filename: string } | null>(null)
  const nav = useNavigate()
  const toast = useToast()
  const vocabulary = useVocabulary(open)

  const set = (k: keyof NewContact) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const reset = () => {
    setForm({ ...EMPTY, status: initialStatus })
    setProfile({})
    setConsent(false)
    setError('')
    setExisting(null)
  }
  const close = () => {
    reset()
    onClose()
  }

  const hasName = Boolean(form.first_name?.trim() || form.last_name?.trim())
  const canSave = hasName && !busy

  async function save(e?: React.FormEvent) {
    e?.preventDefault()
    if (!canSave) return
    setBusy(true)
    setError('')
    setExisting(null)
    try {
      const payload: NewContact = {
        ...form,
        // `referred_by_name` is only for showing the chosen partner in the form.
        profile: Object.keys(profile).length
          ? Object.fromEntries(Object.entries(profile).filter(([k]) => k !== 'referred_by_name'))
          : undefined,
        sms_consent: consent && form.phone && form.phone_kind === 'mobile' ? { basis } : undefined,
      }
      const { id } = await api.createContact(payload)
      if (cardImage) {
        // Best effort: the contact exists either way, and the photo is a nicety.
        await api.addAttachment({ contact_id: id }, { ...cardImage, is_card: true }).catch(() => {})
      }
      toast('Contact added', { tone: 'positive' })
      close()
      nav(`/contacts/c/${id}`)
    } catch (err) {
      if (err instanceof ApiError && err.isConflict && err.existingId) {
        setExisting(err.existingId)
        setError(err.message)
      } else {
        setError(errorText(err))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add contact"
      subtitle="Adds them to the lead book. Nothing is sent to them."
      width={640}
      footer={
        <>
          <button className="btn" onClick={close}>Cancel</button>
          <button className="btn primary" onClick={() => save()} disabled={!canSave}>
            {busy ? 'Saving…' : 'Add contact'}
          </button>
        </>
      }
    >
      <label className={`dropzone sm span-2${scanning ? ' busy' : ''}`} style={{ marginBottom: 16 }}>
        <input type="file" hidden accept="image/*" capture="environment" disabled={scanning}
          onChange={async (e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (!file) return
            setScanning(true)
            setError('')
            try {
              const buffer = await file.arrayBuffer()
              let binary = ''
              new Uint8Array(buffer).forEach((b) => { binary += String.fromCharCode(b) })
              const data_base64 = btoa(binary)
              const { fields } = await api.scanBusinessCard(data_base64, file.type || 'image/jpeg')
              setCardImage({ data_base64, content_type: file.type || 'image/jpeg', filename: file.name || 'business-card.jpg' })
              setForm((f) => ({
                ...f,
                first_name: fields.first_name || f.first_name,
                last_name: fields.last_name || f.last_name,
                email: fields.email || f.email,
                phone: fields.mobile || fields.phone || f.phone,
                phone_kind: fields.mobile ? 'mobile' : f.phone_kind,
                address: fields.address || f.address,
                city: fields.city || f.city,
                state: fields.state || f.state,
                zip: fields.zip || f.zip,
              }))
              setProfile((pr) => ({ ...pr, ...(fields.linkedin ? { linkedin: fields.linkedin } : {}),
                                   ...(fields.website ? { website: fields.website } : {}) }))
              setScanned([fields.first_name, fields.last_name].filter(Boolean).join(' ') || file.name)
            } catch (err) {
              setError(errorText(err))
            } finally {
              setScanning(false)
            }
          }} />
        <Icon name="photo_camera" size={16} />
        <span>{scanning ? 'Reading the card…' : scanned ? `Read from a card: ${scanned} — check it below` : 'Scan a business card'}</span>
      </label>

      <form onSubmit={save} className="form-grid">
        <label className="fl"><span>First name</span>
          <input className="field" value={form.first_name} onChange={set('first_name')} autoComplete="off" /></label>
        <label className="fl"><span>Last name</span>
          <input className="field" value={form.last_name} onChange={set('last_name')} autoComplete="off" /></label>
        <label className="fl"><span>Email</span>
          <input className="field" type="email" value={form.email} onChange={set('email')} autoComplete="off" /></label>
        <div className="fl">
          <span>Phone</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="field" type="tel" value={form.phone} onChange={set('phone')} autoComplete="off" style={{ flex: 1 }} />
            <select className="field" value={form.phone_kind} onChange={set('phone_kind')} style={{ width: 108 }} aria-label="Phone type">
              <option value="mobile">Mobile</option>
              <option value="landline">Landline</option>
            </select>
          </div>
        </div>
        <label className="fl span-2"><span>Street address</span>
          <input className="field" value={form.address} onChange={set('address')} autoComplete="off" /></label>
        <div className="span-2" style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.6fr 0.8fr 1fr', gap: 10 }}>
          <label className="fl"><span>City</span><input className="field" value={form.city} onChange={set('city')} /></label>
          <label className="fl"><span>State</span><input className="field" value={form.state} onChange={set('state')} maxLength={2} /></label>
          <label className="fl"><span>ZIP</span><input className="field" value={form.zip} onChange={set('zip')} maxLength={10} /></label>
          <label className="fl"><span>Stage</span>
            <select className="field" value={form.status} onChange={set('status')}>
              {LEAD_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>
        <label className="fl span-2"><span>Note <span className="hint">— optional, saved to their timeline</span></span>
          <textarea className="field" rows={2} value={form.notes} onChange={set('notes')} style={{ minHeight: 56 }} /></label>

        <div className="span-2" style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Their loan goal</div>
          <div className="hint" style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '2px 0 10px' }}>
            All optional — fill in what you know. It decides who you call first.
          </div>
          <IntakeFields value={profile} onChange={setProfile} vocabulary={vocabulary} />
        </div>

        {form.phone && form.phone_kind === 'mobile' ? (
          <div className="span-2" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className="checkrow">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>They've consented to receive texts at this number</span>
            </label>
            {consent ? (
              <select className="field" value={basis} onChange={(e) => setBasis(e.target.value as ConsentBasis)} aria-label="Consent basis">
                {CONSENT_BASES.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select>
            ) : (
              <span className="hint" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                Without recorded consent the CRM will not text this number — calls and email are unaffected.
              </span>
            )}
          </div>
        ) : null}

        {error ? (
          <div className={`notice ${existing ? 'warning' : 'danger'} span-2`}>
            <Icon name={existing ? 'person_search' : 'error'} />
            <div style={{ flex: 1 }}>
              {error}
              {existing ? (
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="btn sm" onClick={() => { close(); nav(`/contacts/c/${existing}`) }}>
                    Open existing contact
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}
